'use client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from 'components/Loader';
import { useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import { Button, ButtonLink } from '~/components/ui';
import { useApi } from '../../hooks/useApi';
import { Plan } from './types';

export const PlanList = ({ onlyOffers = false }: { onlyOffers?: boolean }) => {
    const { data, error, mutate } = useSWR<{ data: Plan[] }>(`plans`);

    if (onlyOffers) {
        const offers = data?.data?.filter((plan) => !plan.isActive && !plan.onGracePeriod) ?? [];

        return offers.length > 0 ? (
            <div className="tw-flex tw-flex-col tw-gap-3">
                {offers.map((plan) => (
                    <PlanItem key={plan.id} plan={plan} mutate={mutate} />
                ))}
            </div>
        ) : null;
    }

    return (
        <Loader isLoading={!data && !error}>
            <div className="tw-flex tw-flex-col tw-gap-3">
                {data?.data?.length > 0 ? (
                    data?.data?.map((plan) => <PlanItem key={plan.id} plan={plan} mutate={mutate} />)
                ) : (
                    <NoSubscriptionMessage />
                )}
            </div>
        </Loader>
    );
};

const NoSubscriptionMessage = () => {
    return (
        <div className="tw-card-bordered">
            <h4 className="tw-mb-1 tw-text-lg tw-font-extrabold tw-text-slate-900">
                Du hast leider noch kein Sportision-Abo abgeschlossen.
            </h4>
            <p className="tw-mb-4 tw-text-sm tw-text-slate-600">
                Kontaktiere uns und profitiere von deinem individuellen Sportision-Paket. Unsere Mitarbeiter stellen
                gerne ein Bundle an Funktionen zusammen.
            </p>
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
                <Button.Link href="mailto:info@sportision.de">
                    <FontAwesomeIcon icon="envelope" />
                    per Mail
                </Button.Link>
                <Button.Link color="light" href="tel:029228751440">
                    <FontAwesomeIcon icon="phone-alt" />
                    per Telefon
                </Button.Link>
            </div>
        </div>
    );
};

const PlanItem = ({ plan, mutate }: { plan: Plan; mutate: KeyedMutator<{ data: Plan[] }> }) => {
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);

    const handleAction = async (method: 'DELETE' | 'PUT') => {
        setIsLoading(true);
        await api({
            url: `subscriptions/${plan.subscriptionId}`,
            method,
            onBefore: () => setIsLoading(false),
            onSuccess: () => mutate()
        });
    };

    const renderActionButton = () => {
        if (plan.onGracePeriod)
            return (
                <Button isLoading={isLoading} onPress={() => handleAction('PUT')} size="sm">
                    Abo erneut starten
                </Button>
            );
        if (plan.isActive)
            return (
                <Button color="error" isLoading={isLoading} onPress={() => handleAction('DELETE')} size="sm">
                    Jetzt Kündigen
                </Button>
            );

        return (
            <ButtonLink href={`/checkout?plan=${plan.name}`} color="success" size="sm">
                Jetzt Buchen
            </ButtonLink>
        );
    };

    return (
        <div className={`tw-card-bordered`}>
            <div className="tw-flex tw-flex-col tw-justify-between tw-gap-4 sm:tw-flex-row sm:tw-items-start">
                <div className="tw-flex-1">
                    <h3 className="tw-text-lg tw-font-extrabold tw-text-slate-900">{plan.title}</h3>
                    <p className="tw-mb-4 tw-text-sm tw-text-slate-500">{plan.description}</p>

                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                        {renderActionButton()}
                        {plan.onGracePeriod && (
                            <small className="tw-text-xs tw-text-slate-400">
                                Dein Abo wird zum {new Date(plan.cancelAt).toLocaleDateString('de-DE')} gekündigt.
                            </small>
                        )}
                    </div>
                </div>

                <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-end">
                    <h5 className="tw-whitespace-nowrap tw-text-lg tw-font-bold tw-text-slate-900">{plan.amount} €</h5>
                    <span className="tw-text-xs tw-text-slate-400">/ Monat</span>
                </div>
            </div>
        </div>
    );
};
